import { useEffect, useMemo, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { narratorAudiobooks } from './data'
import { getSortedBooks, chunkBooksIntoShelves } from './utils'

export function useNarratorShelf() {
  const navigate = useNavigate()
  const { category } = useParams() // 'series', 'author', 'special'

  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedGenre, setSelectedGenre] = useState('')
  const [viewMode, setViewMode] = useState('spine')
  const [sortOption, setSortOption] = useState('seriesOrder')
  const [darkMode, setDarkMode] = useState(false)
  const [hoveredBookId, setHoveredBookId] = useState<string | null>(null)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [booksPerShelf, setBooksPerShelf] = useState(12)
  const [selectedWrittenAuthor, setSelectedWrittenAuthor] = useState('all')

  const shelfContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true'
    setDarkMode(savedDarkMode)

    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }

    const calculateShelfCapacity = () => {
      if (shelfContainerRef.current) {
        const containerWidth = shelfContainerRef.current.offsetWidth
        const estimatedBooksPerShelf = Math.floor(containerWidth / 60)
        setBooksPerShelf(Math.max(estimatedBooksPerShelf, 5))
      }
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('resize', calculateShelfCapacity)
    calculateShelfCapacity()

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('resize', calculateShelfCapacity)
    }
  }, [])

  const genres = useMemo(
    () => [...new Set(narratorAudiobooks.map((b) => b.genre))],
    [],
  )

  const filteredBooks = useMemo(() => {
    return narratorAudiobooks.filter((book) => {
      const matchesSearch =
        book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        book.writtenAuthor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (book.series &&
          book.series.toLowerCase().includes(searchTerm.toLowerCase()))

      const matchesGenre = selectedGenre === '' || book.genre === selectedGenre

      // Category filtering logic
      if (category === 'special')
        return matchesSearch && matchesGenre && book.isSpecialProject

      return matchesSearch && matchesGenre
    })
  }, [searchTerm, selectedGenre, category])

  const sortedBooks = useMemo(
    () => getSortedBooks(filteredBooks, sortOption),
    [filteredBooks, sortOption],
  )

  const bookShelves = useMemo(
    () => chunkBooksIntoShelves(sortedBooks, booksPerShelf),
    [sortedBooks, booksPerShelf],
  )

  // Group by Series
  const seriesData = useMemo(() => {
    const seriesList: Record<string, any[]> = {}
    narratorAudiobooks.forEach((book) => {
      if (book.series) {
        if (!seriesList[book.series]) seriesList[book.series] = []
        seriesList[book.series].push(book)
      }
    })
    // Sort within series
    Object.values(seriesList).forEach((books) => {
      books.sort((a, b) => (a.seriesNumber || 0) - (b.seriesNumber || 0))
    })
    return seriesList
  }, [])

  // Group by Written Author
  const authorData = useMemo(() => {
    const authorList: Record<string, any[]> = {}
    narratorAudiobooks.forEach((book) => {
      if (!authorList[book.writtenAuthor]) authorList[book.writtenAuthor] = []
      authorList[book.writtenAuthor].push(book)
    })
    return authorList
  }, [])

  const writtenAuthors = useMemo(
    () => ['all', ...Object.keys(authorData).sort()],
    [authorData],
  )

  const filteredAuthorBooks = useMemo(() => {
    if (selectedWrittenAuthor === 'all') return narratorAudiobooks
    return narratorAudiobooks.filter(
      (b) => b.writtenAuthor === selectedWrittenAuthor,
    )
  }, [selectedWrittenAuthor])

  const navigateToCategory = (cat: string) => navigate(`/narrator/shelf/${cat}`)
  const navigateToEdit = (id: string) => navigate(`/book/${id}/edit`)

  return {
    category,
    searchTerm,
    setSearchTerm,
    showFilters,
    setShowFilters,
    selectedGenre,
    setSelectedGenre,
    viewMode,
    setViewMode,
    sortOption,
    setSortOption,
    darkMode,
    hoveredBookId,
    setHoveredBookId,
    mousePosition,
    booksPerShelf,
    shelfContainerRef,
    selectedWrittenAuthor,
    setSelectedWrittenAuthor,
    genres,
    sortedBooks,
    bookShelves,
    seriesData,
    authorData,
    writtenAuthors,
    filteredAuthorBooks,
    navigateToCategory,
    navigateToEdit,
    navigate,
  }
}
